<script lang="ts">
  import { Laboratorio, Imagenologia, Compartido,
    type OrdenLab, type OrdenLabDetalle, type OrdenImagen, type OrdenImagenDetalle,
    type EstadoCuentaCompartido } from '../lib/api';

  let cargando = $state(true);
  let error = $state('');

  let lab = $state<OrdenLab[]>([]);
  let imagen = $state<OrdenImagen[]>([]);
  let estadoCuenta = $state<EstadoCuentaCompartido[]>([]);

  let labDetalle = $state<OrdenLabDetalle | null>(null);
  let imagenDetalle = $state<OrdenImagenDetalle | null>(null);

  async function cargar() {
    cargando = true; error = '';
    try {
      const [l, i, e] = await Promise.all([
        Laboratorio.compartidoOrdenes().catch(() => []),
        Imagenologia.compartidoOrdenes().catch(() => []),
        Compartido.estadoCuenta().catch(() => []),
      ]);
      lab = l; imagen = i; estadoCuenta = e;
    } catch (e: any) {
      error = e?.message ?? 'Error cargando datos compartidos';
    } finally {
      cargando = false;
    }
  }

  async function verLab(id?: string) {
    if (!id) return;
    imagenDetalle = null;
    try { labDetalle = await Laboratorio.compartidoOrden(id); }
    catch (e: any) { error = e?.message ?? 'No se pudo abrir la orden'; }
  }
  async function verImagen(id?: string) {
    if (!id) return;
    labDetalle = null;
    try { imagenDetalle = await Imagenologia.compartidoOrden(id); }
    catch (e: any) { error = e?.message ?? 'No se pudo abrir la orden'; }
  }

  $effect(() => { cargar(); });
</script>

<section class="compartido">
  <header class="head">
    <div>
      <h3>Resultados compartidos (como clínica aliada)</h3>
      <p class="hint">Órdenes, informes y estado de cuenta que los hospitales aliados comparten con esta
        clínica vía un acuerdo <strong>aceptado</strong>. Solo-lectura.</p>
    </div>
    <button onclick={cargar} disabled={cargando}>{cargando ? 'Cargando…' : 'Actualizar'}</button>
  </header>

  {#if error}<p class="err">{error}</p>{/if}

  <div class="grid">
    <div class="col">
      <h4>Laboratorio ({lab.length})</h4>
      <table>
        <thead><tr><th>N.º</th><th>Paciente</th><th>Estado</th><th>Total</th><th></th></tr></thead>
        <tbody>
          {#each lab as o (o.id)}
            <tr>
              <td>{o.numero ?? '—'}</td>
              <td>{o.pacienteNombre ?? '—'}</td>
              <td>{o.estado ?? '—'}</td>
              <td>Q{o.total ?? 0}</td>
              <td><button class="link" onclick={() => verLab(o.id)}>Ver</button></td>
            </tr>
          {/each}
          {#if lab.length === 0}<tr><td colspan="5">Sin órdenes de laboratorio compartidas.</td></tr>{/if}
        </tbody>
      </table>
    </div>

    <div class="col">
      <h4>Imagenología ({imagen.length})</h4>
      <table>
        <thead><tr><th>N.º</th><th>Paciente</th><th>Estado</th><th>Total</th><th></th></tr></thead>
        <tbody>
          {#each imagen as o (o.id)}
            <tr>
              <td>{o.numero ?? '—'}</td>
              <td>{o.pacienteNombre ?? '—'}</td>
              <td>{o.estado ?? '—'}</td>
              <td>Q{o.total ?? 0}</td>
              <td><button class="link" onclick={() => verImagen(o.id)}>Ver</button></td>
            </tr>
          {/each}
          {#if imagen.length === 0}<tr><td colspan="5">Sin órdenes de imagenología compartidas.</td></tr>{/if}
        </tbody>
      </table>
    </div>
  </div>

  {#if labDetalle}
    <div class="detalle">
      <h4>Orden de laboratorio {labDetalle.orden.numero} — {labDetalle.orden.pacienteNombre}</h4>
      <table>
        <thead><tr><th>Examen</th><th>Valor</th><th>Ref.</th><th>Flag</th></tr></thead>
        <tbody>
          {#each labDetalle.examenes as ex (ex.id)}
            <tr class:alerta={ex.flag === 'ALTO' || ex.flag === 'BAJO' || ex.flag === 'CRITICO'}>
              <td>{ex.examenNombre}</td>
              <td>{ex.valor ?? '—'} {ex.unidad ?? ''}</td>
              <td>{ex.valorRefTexto ?? `${ex.valorRefMin ?? ''}–${ex.valorRefMax ?? ''}`}</td>
              <td>{ex.flag ?? '—'}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}

  {#if imagenDetalle}
    <div class="detalle">
      <h4>Estudio de imagen {imagenDetalle.orden.numero} — {imagenDetalle.orden.pacienteNombre}</h4>
      <p><strong>Hallazgos:</strong> {imagenDetalle.orden.hallazgos ?? '—'}</p>
      <p><strong>Impresión:</strong> {imagenDetalle.orden.impresion ?? '—'}</p>
      <table>
        <thead><tr><th>Estudio</th><th>Modalidad</th><th>Región</th></tr></thead>
        <tbody>
          {#each imagenDetalle.estudios as es (es.id)}
            <tr><td>{es.estudioNombre}</td><td>{es.modalidad ?? '—'}</td><td>{es.region ?? '—'}</td></tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}

  <h4>Estado de cuenta con hospitales aliados</h4>
  <table>
    <thead><tr><th>Hospital (tenant)</th><th>Cargos</th><th>Abonos</th><th>Saldo</th><th>Estado</th></tr></thead>
    <tbody>
      {#each estadoCuenta as e (e.consultorioId)}
        <tr>
          <td><code>{e.hospitalTenantId}</code></td>
          <td>Q{e.totalCargos}</td>
          <td>Q{e.totalAbonos}</td>
          <td><strong>Q{e.saldo}</strong></td>
          <td>{e.estadoCuenta}</td>
        </tr>
      {/each}
      {#if estadoCuenta.length === 0}<tr><td colspan="5">Sin estado de cuenta con hospitales aliados.</td></tr>{/if}
    </tbody>
  </table>
</section>

<style>
  .head { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; flex-wrap: wrap; margin-bottom: 1rem; }
  .head h3 { margin: 0 0 .25rem; font-size: 1.3rem; color: #111827; }
  .head button { padding: .5rem .9rem; border: 0; background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; border-radius: 8px; cursor: pointer; font: inherit; font-weight: 600; box-shadow: 0 4px 14px rgba(22,163,74,0.3); transition: transform 0.15s; }
  .head button:hover:not(:disabled) { transform: translateY(-1px); }
  .head button:disabled { opacity: .55; cursor: not-allowed; box-shadow: none; }
  .hint { color: #6b7280; font-size: .85rem; margin: .25rem 0 1rem; max-width: 60ch; }
  .err { color: #dc2626; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 1rem; font-size: .9rem; }
  th { text-align: left; padding: .5rem .8rem; color: #6b7280; font-weight: 600; font-size: .7rem; text-transform: uppercase; letter-spacing: .03em; background: #f0fdf4; }
  td { text-align: left; padding: .45rem .8rem; border-top: 1px solid #f3f4f6; color: #111827; }
  .link { border: 0; background: transparent; color: #16a34a; cursor: pointer; text-decoration: underline; font: inherit; }
  .detalle { background: #f0fdf4; border: 1px solid #e5e7eb; border-radius: 8px; padding: .75rem 1rem; margin-bottom: 1rem; }
  .detalle h4 { margin: 0 0 .5rem; color: #166534; }
  tr.alerta td { background: #fef2f2; }
  h4 { color: #166534; }
  code { background: #f3f4f6; padding: .1rem .35rem; border-radius: 4px; font-size: .8rem; color: #111827; }
  @media (max-width: 720px) { .grid { grid-template-columns: 1fr; } }
</style>
